import { useEffect, useState, useSyncExternalStore } from 'react';

export type SharedResourceSnapshot<T> = Readonly<{
	owner: symbol | null;
	generation: number;
	value: T | null;
	error: Error | null;
}>;

/** Coordinates React owners and references; session records remain in Relay. */
export class SharedAgentSessionsResource<T> {
	private readers = new Set<symbol>();
	private listeners = new Set<() => void>();
	private snapshot: SharedResourceSnapshot<T> = {
		owner: null,
		generation: 0,
		value: null,
		error: null,
	};
	readonly getSnapshot = (): SharedResourceSnapshot<T> => this.snapshot;
	readonly subscribe = (listener: () => void): (() => void) => {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	};
	private update(update: Partial<SharedResourceSnapshot<T>>): void {
		this.snapshot = { ...this.snapshot, ...update };
		this.listeners.forEach((listener) => listener());
	}
	acquire(reader: symbol): () => void {
		this.readers.add(reader);
		if (!this.snapshot.owner)
			this.update({ owner: reader, generation: this.snapshot.generation + 1, error: null });
		let released = false;
		return () => {
			if (released) return;
			released = true;
			this.readers.delete(reader);
			if (this.snapshot.owner === reader) {
				this.update({
					owner: this.readers.values().next().value ?? null,
					generation: this.snapshot.generation + 1,
				});
			}
		};
	}
	public publish(owner: symbol, generation: number, value: T): void {
		if (this.snapshot.owner !== owner || this.snapshot.generation !== generation) return;
		this.update({ value, error: null });
	}
	public fail(owner: symbol, generation: number, error: Error): void {
		if (this.snapshot.owner !== owner || this.snapshot.generation !== generation) return;
		this.update({ error });
	}
	public clear(): void {
		this.update({ value: null, error: null });
	}
	public owns(owner: symbol, generation: number): boolean {
		return this.snapshot.owner === owner && this.snapshot.generation === generation;
	}
}

export const useSharedAgentSessionsResource = <T>(
	resource: SharedAgentSessionsResource<T>,
	active = true,
): SharedResourceSnapshot<T> & { reader: symbol; isOwner: boolean } => {
	const [reader] = useState(() => Symbol('agent-sessions-reader'));
	const snapshot = useSyncExternalStore(
		resource.subscribe,
		resource.getSnapshot,
		resource.getSnapshot,
	);
	useEffect(() => (active ? resource.acquire(reader) : undefined), [active, reader, resource]);
	return { ...snapshot, reader, isOwner: active && snapshot.owner === reader };
};
